/**
 * Plan complexe, mode « Opérations » : deux points M₁(z₁) et M₂(z₂) qu'on
 * déplace ; somme (parallélogramme), produit (les arguments s'ajoutent, les
 * modules se multiplient), quotient, distance M₁M₂ = |z₂ − z₁|.
 */

import { useRef, useState } from 'react';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, Segmented } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { snapPoint } from '../../ui/frame';
import { DragHandle, HaloText, INK, VectorArrow } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  modAtoms,
  modPlain,
  modulusQ,
  operate,
  opSteps,
  zPlain,
  zTex,
  type Op,
  type Z,
} from './complex';
import {
  Formula,
  Live,
  Plane,
  PointMark,
  RES_COLOR,
  SOFT,
  StepList,
  VP,
  Z1_COLOR,
  Z2_COLOR,
  arcPath,
  frame,
  keepInside,
  outward,
  inView,
  LABEL_MARGIN,
  px,
} from './parts';

const OPS: ReadonlyArray<{ value: Op; label: string }> = [
  { value: 'sum', label: 'Somme' },
  { value: 'product', label: 'Produit' },
  { value: 'quotient', label: 'Quotient' },
  { value: 'distance', label: 'Distance' },
];

const RES_LABEL: Record<Op, string> = {
  sum: 'z₁ + z₂',
  product: 'z₁z₂',
  quotient: 'z₁/z₂',
  distance: 'z₂ − z₁',
};

const OUT_HINT: Record<Op, string> = {
  sum: 'rapproche M₁ ou M₂ de O',
  product: 'rapproche M₁ ou M₂ de O',
  quotient: 'rapproche M₁ de O, ou éloigne M₂ de O',
  distance: 'rapproche M₁ et M₂ l’un de l’autre',
};

type Id = 'z1' | 'z2';

export function OpsMode({ onSend, sendCost }: ToolProps) {
  const [z1, setZ1] = useState<Z>({ a: 2, b: 1 });
  const [z2, setZ2] = useState<Z>({ a: 1, b: 1 });
  const [op, setOp] = useState<Op>('sum');
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useDrag<Id>({
    svgRef,
    frame,
    ids: ['z1', 'z2'],
    pointOf: (id) => {
      const z = id === 'z1' ? z1 : z2;
      return { x: z.a, y: z.b };
    },
    onDrag: (id, p) => {
      const q = snapPoint(p, VP, 0.5, 0.5);
      (id === 'z1' ? setZ1 : setZ2)({ a: q.x, b: q.y });
    },
    keyStep: 0.5,
  });

  const r = operate(op, z1, z2);
  const res: Z | null = r ? { a: r.re.p / r.re.q, b: r.im.p / r.im.q } : null;
  const O = px({ a: 0, b: 0 });
  const P1 = px(z1);
  const P2 = px(z2);
  const PR = res ? px(res) : null;
  const t1 = Math.atan2(z1.b, z1.a);
  const t2 = Math.atan2(z2.b, z2.a);
  const tr = res ? Math.atan2(res.b, res.a) : 0;
  const nonZero = (z: Z) => z.a !== 0 || z.b !== 0;
  const out = res && !inView(res, frame, LABEL_MARGIN);
  const dist = op === 'distance' && r ? modulusQ(r.re, r.im) : null;
  const lab1 = keepInside(P1.x, P1.y, outward(z1, 20));
  const lab2 = keepInside(P2.x, P2.y, outward(z2, 20));

  return (
    <div className="space-y-4">
      <Segmented ariaLabel="Opération" value={op} options={OPS} onChange={setOp} />

      <Plane
        svgRef={svgRef}
        label={`Plan complexe : M₁ d’affixe z₁ = ${zPlain(z1)}, M₂ d’affixe z₂ = ${zPlain(z2)}${
          res ? `, résultat ${zPlain(res)}` : ''
        }`}
        svgProps={drag.svgProps}
      >
        {op === 'sum' && res && PR && (
          <>
            <line x1={P1.x} y1={P1.y} x2={PR.x} y2={PR.y} stroke={Z2_COLOR} strokeDasharray="5 4" />
            <line x1={P2.x} y1={P2.y} x2={PR.x} y2={PR.y} stroke={Z1_COLOR} strokeDasharray="5 4" />
          </>
        )}
        {op === 'product' && res && nonZero(z1) && nonZero(z2) && (
          <>
            <path d={arcPath(O.x, O.y, 24, 0, t1)} fill="none" stroke={Z1_COLOR} strokeWidth={2} />
            <path d={arcPath(O.x, O.y, 34, t1, tr)} fill="none" stroke={Z2_COLOR} strokeWidth={2} />
            <path
              d={arcPath(O.x, O.y, 44, 0, t2)}
              fill="none"
              stroke={Z2_COLOR}
              strokeWidth={1.25}
              strokeDasharray="3 3"
            />
          </>
        )}
        {op === 'quotient' && res && nonZero(z1) && (
          <>
            {/* arg z₁ − arg z₂ (de M₂ à M₁), puis le même angle depuis l'axe réel */}
            <path
              d={arcPath(O.x, O.y, 44, t2, t1)}
              fill="none"
              stroke={RES_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
            <path
              d={arcPath(O.x, O.y, 28, 0, tr)}
              fill="none"
              stroke={RES_COLOR}
              strokeWidth={2.5}
            />
          </>
        )}
        {op === 'distance' ? (
          <>
            <VectorArrow x1={P1.x} y1={P1.y} x2={P2.x} y2={P2.y} color={RES_COLOR} inset={9} />
            {res && PR && (
              <VectorArrow
                x1={O.x}
                y1={O.y}
                x2={PR.x}
                y2={PR.y}
                color={RES_COLOR}
                width={2}
                dashed
                inset={6}
              />
            )}
          </>
        ) : (
          <>
            <VectorArrow x1={O.x} y1={O.y} x2={P1.x} y2={P1.y} color={Z1_COLOR} inset={8} />
            <VectorArrow x1={O.x} y1={O.y} x2={P2.x} y2={P2.y} color={Z2_COLOR} inset={8} />
            {res && PR && nonZero(res) && (
              <VectorArrow
                x1={O.x}
                y1={O.y}
                x2={PR.x}
                y2={PR.y}
                color={RES_COLOR}
                width={3.5}
                inset={6}
              />
            )}
          </>
        )}
        {res && <PointMark z={res} color={RES_COLOR} label={RES_LABEL[op]} testId="cp-result" />}
        <HaloText x={O.x - 13} y={O.y + 16} fontSize={14} fontWeight={700} fill={INK} aria-hidden>
          O
        </HaloText>
        <circle cx={O.x} cy={O.y} r={2.5} fill={SOFT} aria-hidden />
        <DragHandle
          x={P1.x}
          y={P1.y}
          color={Z1_COLOR}
          label="z₁"
          labelDx={lab1.dx}
          labelDy={lab1.dy}
          name={`Point M₁ d’affixe z₁ = ${zPlain(z1)}`}
          active={drag.active === 'z1'}
          keyProps={drag.keyProps('z1')}
        />
        <DragHandle
          x={P2.x}
          y={P2.y}
          color={Z2_COLOR}
          label="z₂"
          labelDx={lab2.dx}
          labelDy={lab2.dy}
          name={`Point M₂ d’affixe z₂ = ${zPlain(z2)}`}
          active={drag.active === 'z2'}
          keyProps={drag.keyProps('z2')}
        />
      </Plane>

      <Live
        text={
          res
            ? op === 'distance' && dist
              ? `M₁M₂ = ${modPlain(dist)}`
              : `${RES_LABEL[op]} = ${zPlain(res)}`
            : 'Division par 0 impossible'
        }
      />
      <div className="space-y-2" data-testid="cp-ops">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Formula tex={`z_1 = ${zTex(z1)}`} />
          <Formula tex={`z_2 = ${zTex(z2)}`} />
        </div>
        <StepList steps={opSteps(op, z1, z2)} />
        {dist && onSend && (
          <div className="flex justify-end">
            <SendToSheetButton
              label={modPlain(dist)}
              cost={sendCost}
              onClick={() => onSend({ atoms: modAtoms(dist) })}
            />
          </div>
        )}
        {out && (
          <Note tone="neutral">
            {frenchSpaces(`Le point ${RES_LABEL[op]} sort de la figure : ${OUT_HINT[op]}.`)}
          </Note>
        )}
      </div>
    </div>
  );
}
